//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { useState } from 'react';
import { IActionSheetButton } from './ActionSheet';

interface IActionSheetNavigation {
  navigateToChildSheet: (idx: number) => void;
  navigateToParentSheet: () => void;
  navigateToRootSheet: () => void;
}

interface IActionSheetState {
  activeButton: IActionSheetButton;
  activeButtonHasParent: boolean;
}

export const useActionSheet = (
  actionSheetConfig: IActionSheetButton[],
): [IActionSheetState, IActionSheetNavigation] => {
  const [activeIndices, setActiveIndices] = useState<number[]>([]);

  // Create a default root "button". This is not clickable
  // and acts as a root sheet that only renders its children
  let activeButton: IActionSheetButton = {
    // special case: this is the only valid undefined value for `label`.
    label: undefined as unknown as string,
    children: actionSheetConfig,
  };

  // Select the activeButton using the activeIndices value
  activeIndices.forEach((idx) => (activeButton = activeButton.children![idx]));

  const activeButtonHasParent = activeIndices.length >= 1;

  const navigateToChildSheet = (idx: number) => {
    if (activeButton.children?.[idx]) {
      setActiveIndices(activeIndices.concat(idx));
    }
  };

  const navigateToParentSheet = () => {
    setActiveIndices(activeIndices.slice(0, activeIndices.length - 1));
  };

  const navigateToRootSheet = () => {
    setActiveIndices([]);
  };

  return [
    {
      activeButton,
      activeButtonHasParent,
    },
    {
      navigateToChildSheet,
      navigateToParentSheet,
      navigateToRootSheet,
    },
  ];
};

export default useActionSheet;
